import { useEffect, useState, useCallback, useMemo, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { api, auth } from "@/lib/api";
import AuthPage from "@/pages/AuthPage";
import OnboardingPage from "@/pages/OnboardingPage";
import DashboardPage from "@/pages/DashboardPage";
import VaultPage from "@/pages/VaultPage";
import AIChatPage from "@/pages/AIChatPage";
import FamilyChatPage from "@/pages/FamilyChatPage";
import ShoppingPage from "@/pages/ShoppingPage";
import CalendarPage from "@/pages/CalendarPage";
import SettingsPage from "@/pages/SettingsPage";
import FeedPage from "@/pages/FeedPage";
import PricingPage from "@/pages/PricingPage";
import AdminDashboardPage from "@/pages/AdminDashboardPage";
import LandingPage from "@/pages/LandingPage";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";
import DeleteAccountPage from "@/pages/DeleteAccountPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AppLayout from "@/components/AppLayout";
import InstallPwaButton from "@/components/InstallPwaButton";

export const SessionContext = createContext(null);
export const useSession = () => useContext(SessionContext);

const ProtectedRoute = ({ children }) => {
  const session = useSession();
  const location = useLocation();
  if (!session?.user) return <Navigate to="/auth" state={{ from: location }} replace />;
  return children;
};

function App() {
  const [user, setUser] = useState(auth.getUser());
  const [family, setFamily] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  const refreshSession = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setFamily(data.family);
      setMembers(data.members);
      auth.saveSession(auth.getToken(), data.user);
    } catch (err) {
      console.error("Session refresh failed:", err?.response?.status || err?.message);
      auth.logout();
      setUser(null);
    }
  }, []);

  useEffect(() => {
    if (auth.getToken()) {
      refreshSession().finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
    // Register service worker for PWA
    if ("serviceWorker" in navigator && process.env.NODE_ENV === "production") {
      window.addEventListener("load", () => {
        navigator.serviceWorker.register("/sw.js").catch((err) => {
          console.warn("Service worker registration failed:", err);
        });
      });
    }
  }, [refreshSession]);

  const login = async (token, u) => {
    auth.saveSession(token, u);
    setUser(u);
    await refreshSession();
  };

  const logout = () => {
    auth.logout();
    setUser(null);
    setFamily(null);
    setMembers([]);
  };

  // Memoise context value so consumers don't re-render on every parent render.
  // login/logout are stable closures; user/family/members/refreshSession are the
  // real dependencies that should trigger downstream updates.
  const sessionValue = useMemo(
    () => ({ user, family, members, login, logout, refreshSession }),
    [user, family, members, refreshSession]
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-app flex items-center justify-center">
        <div className="text-secondary-fg flex items-center gap-2">
          <span className="typing-dot" />
          <span className="typing-dot" />
          <span className="typing-dot" />
        </div>
      </div>
    );
  }

  return (
    <SessionContext.Provider value={sessionValue}>
      <Toaster theme="dark" position="top-center" richColors />
      <InstallPwaButton />
      <BrowserRouter>
        <Routes>
          <Route path="/auth" element={user ? <Navigate to="/app" replace /> : <AuthPage />} />
          <Route path="/" element={user ? <Navigate to="/app" replace /> : <LandingPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/delete-account" element={<DeleteAccountPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/pricing-public" element={<PricingPage />} />
          <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
          <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
            <Route path="/app" element={<DashboardPage />} />
            <Route path="/vault" element={<VaultPage />} />
            <Route path="/ai" element={<AIChatPage />} />
            <Route path="/chat" element={<FamilyChatPage />} />
            <Route path="/feed" element={<FeedPage />} />
            <Route path="/shopping" element={<ShoppingPage />} />
            <Route path="/calendar" element={<CalendarPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/admin" element={<AdminDashboardPage />} />
            <Route path="/settings" element={<SettingsPage />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </SessionContext.Provider>
  );
}

export default App;
